// src/components/financials/AmendmentDiff.jsx
// SCREENER-FINANCIALS — new file. Field-by-field "what changed" view for an
// amended period. Deliberately honest about every non-ideal state rather
// than showing a blank or misleading result — AMENDSID linkage has never
// been confirmed working against real production data (HANDOVER.md), so
// this WILL legitimately hit its fallback/not-found paths on real data
// until a full recapture confirms otherwise.

import React from 'react';

function fmtCompact(n) {
  if (n == null) return '—';
  const abs = Math.abs(Number(n));
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return sign + '$' + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return sign + '$' + (abs / 1e6).toFixed(2) + 'M';
  if (abs >= 1e3) return sign + '$' + (abs / 1e3).toFixed(1) + 'K';
  return sign + '$' + abs.toFixed(2);
}

const FIELD_LABELS = {
  revenue: 'Revenue', costOfRevenue: 'Cost of Revenue', grossProfit: 'Gross Profit',
  operatingExpenses: 'Operating Expenses', operatingIncome: 'Operating Income',
  netIncome: 'Net Income', epsBasic: 'EPS (Basic)', epsDiluted: 'EPS (Diluted)',
  sharesOutstandingBasic: 'Shares Outstanding (Basic)', sharesOutstandingDiluted: 'Shares Outstanding (Diluted)',
  totalAssets: 'Total Assets', totalCurrentAssets: 'Total Current Assets',
  totalLiabilities: 'Total Liabilities', totalCurrentLiabilities: 'Total Current Liabilities',
  totalStockholdersEquity: "Stockholders' Equity", longTermDebt: 'Long-Term Debt',
  cashAndEquivalents: 'Cash & Equivalents', retainedEarnings: 'Retained Earnings',
  operatingCashFlow: 'Operating Cash Flow', capex: 'CapEx',
  dividendsPaid: 'Dividends Paid', dividendsPerShare: 'Dividends/Share',
};

const REASON_MESSAGES = {
  NOT_AN_AMENDMENT: 'This period was not amended.',
  NO_ORIGINAL_FOUND: "This is an amendment, but the original filing it replaces hasn't been captured yet — nothing to compare against.",
  NO_FIELD_CHANGES: 'This amendment is on file, but none of the reported figures actually changed from the original — likely a non-financial correction (e.g. exhibits, disclosures).',
  ERROR: 'Unable to load the comparison right now.',
  DEGRADED: 'Unable to load the comparison right now.',
};

export default function AmendmentDiff({ diff, loading, error }) {
  if (loading) {
    return <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>Comparing filings…</div>;
  }
  if (error) {
    return <div style={{ padding: '1rem', textAlign: 'center', color: '#ff4444', fontSize: '0.8rem' }}>{error}</div>;
  }
  if (!diff) return null;

  if (!diff.hasDiff) {
    return (
      <div style={{ padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '10px', fontSize: '0.78rem', color: '#64748b' }}>
        {REASON_MESSAGES[diff.reason] || 'No comparison available.'}
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', background: 'rgba(255,170,0,0.03)', border: '1px solid rgba(255,170,0,0.15)', borderRadius: '10px' }}>
      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.75rem' }}>
        {diff.originalFormType} filed {diff.originalFiledDate} → {diff.amendedFormType} filed {diff.amendedFiledDate}
        {diff.linkageMethod === 'PERIOD_FALLBACK' && (
          <span style={{ color: '#ffaa00' }}> · matched by period, not direct filing linkage — verify against the source filing if precision matters</span>
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <th style={{ textAlign: 'left', padding: '0.4rem 0', fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>Field</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0', fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>Original</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0', fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>Amended</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0', fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>Change</th>
          </tr>
        </thead>
        <tbody>
          {diff.changes.map(c => {
            const isPerShare = c.field.startsWith('eps') || c.field === 'dividendsPerShare';
            const fmt = isPerShare ? (v => v == null ? '—' : '$' + Number(v).toFixed(2)) : fmtCompact;
            const positive = c.pctChange != null && c.pctChange >= 0;
            return (
              <tr key={c.field} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '0.45rem 0', fontSize: '0.8rem', color: '#cbd5e1' }}>{FIELD_LABELS[c.field] || c.field}</td>
                <td style={{ padding: '0.45rem 0', fontSize: '0.8rem', color: '#64748b', textAlign: 'right' }}>{fmt(c.originalValue)}</td>
                <td style={{ padding: '0.45rem 0', fontSize: '0.8rem', color: '#f8fafc', fontWeight: 700, textAlign: 'right' }}>{fmt(c.amendedValue)}</td>
                <td style={{ padding: '0.45rem 0', fontSize: '0.78rem', textAlign: 'right', color: c.pctChange == null ? '#475569' : (positive ? '#00ff88' : '#ff4444') }}>
                  {c.pctChange == null ? '—' : (positive ? '+' : '') + (c.pctChange * 100).toFixed(1) + '%'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
